import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  LiveKitRoom,
  useTracks,
  ParticipantTile,
  useDataChannel,
  useLocalParticipant,
  RoomAudioRenderer,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Users, Heart, Radio, ArrowLeft, Gift, Trophy, MessageSquare, Mic, MicOff, Video, VideoOff, LogOut } from "lucide-react";
import { toast } from "sonner";
import GiftDrawer from "@/components/plyx/GiftDrawer";
import BumperBattlePanel from "@/components/plyx/BumperBattlePanel";
import LiveChatPanel from "@/components/plyx/LiveChatPanel";
import StreakFire from "@/components/plyx/StreakFire";
import StreamGoalWidget from "@/components/plyx/StreamGoalWidget";
import LiveHourChip from "@/components/plyx/LiveHourChip";
import PlyxStagePanel from "@/components/plyx/PlyxStagePanel";

/**
 * Plyx Live viewer — `/plyx/live/:roomName`
 *
 * - Mints a subscriber-only LiveKit token, connects.
 * - Hearts and gift confetti are broadcast over the LiveKit data channel
 *   (`topic: plyx-engagement`), so every viewer + the broadcaster see them
 *   in real time. Local animation fires immediately for the sender so the
 *   round-trip never feels laggy.
 * - Gift drawer hits POST /api/plyx/live/{room}/gift (and .../tip), which
 *   credits the creator with the same 70/30 split as video gifts.
 */
const ViewerStage = () => {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: true },
  );
  if (tracks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white/50">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p className="text-sm">Waiting for the broadcaster…</p>
      </div>
    );
  }
  // Single speaker → fullscreen. Multiple (host + guests) → adaptive
  // grid that stays usable even at 10 tiles. cols = ceil(sqrt(n))
  // gives a balanced layout (1→1, 2→2, 3-4→2, 5-9→3, 10→4).
  const n = tracks.length;
  const cols = n <= 1 ? 1 : Math.ceil(Math.sqrt(n));
  return (
    <div
      className="w-full h-full overflow-hidden grid gap-1 bg-black"
      data-testid="plyx-live-viewer-stage"
      // Force every <video> the LiveKit ParticipantTile renders inside
      // to scale-fit the available area instead of overflowing at its
      // native camera resolution. Without this, a 1080p landscape
      // broadcaster feed renders at 1080px wide and the user has to
      // pinch-zoom out on mobile to see the whole frame.
      style={{
        "--lk-video-object-fit": "cover",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {tracks.map((t) => (
        <ParticipantTile
          key={t.publication?.trackSid || t.participant.identity}
          trackRef={t}
          style={{ height: "100%", width: "100%", borderRadius: 0 }}
        />
      ))}
    </div>
  );
};

/**
 * GuestControls — Mute mic / hide camera / leave stage. Only renders
 * once the local participant has publish permissions (i.e. they're
 * either the host or accepted a stage invite).
 */
const GuestControls = ({ onLeave }) => {
  const { localParticipant } = useLocalParticipant();
  const canPublish = !!localParticipant?.permissions?.canPublish;
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  // Initial publish — when canPublish flips to true (just promoted)
  // turn camera + mic on so the viewer doesn't have to tap anything.
  useEffect(() => {
    if (!canPublish || !localParticipant) return;
    (async () => {
      try {
        await localParticipant.setMicrophoneEnabled(true);
        await localParticipant.setCameraEnabled(true);
        setMicOn(true);
        setCamOn(true);
      } catch (e) {
        toast.error("Couldn't access your camera/mic — check browser permissions");
      }
    })();
  }, [canPublish, localParticipant]);

  // Listen for the host's "please turn on your camera" nudge over the
  // `plyx-stage` data channel. When it arrives, fire a sonner toast
  // with an Accept action so the camera-on call happens inside a fresh
  // user gesture (iOS Safari needs this — see PlyxLiveNew's host
  // controls). Without the gesture round-trip the second
  // setCameraEnabled call silently fails on iOS.
  const stageChannel = useDataChannel("plyx-stage");
  useEffect(() => {
    const msg = stageChannel?.message;
    if (!msg || !canPublish || !localParticipant) return;
    try {
      const txt = new TextDecoder().decode(msg.payload);
      const parsed = JSON.parse(txt);
      if (parsed.type !== "host_request_camera") return;
      // Already on? Acknowledge silently — no need to nag the user.
      if (localParticipant.isCameraEnabled) return;
      toast("The host is asking you to turn your camera on", {
        duration: 30000,
        action: {
          label: "Turn on",
          onClick: async () => {
            try {
              await localParticipant.setCameraEnabled(true);
              setCamOn(true);
            } catch {
              toast.error("Couldn't turn on camera — check browser permissions");
            }
          },
        },
      });
    } catch { /* ignore */ }
  }, [stageChannel?.message, canPublish, localParticipant]);

  if (!canPublish) return null;

  const toggleMic = async () => {
    try {
      await localParticipant.setMicrophoneEnabled(!micOn);
      setMicOn((v) => !v);
    } catch { /* ignore */ }
  };
  const toggleCam = async () => {
    try {
      await localParticipant.setCameraEnabled(!camOn);
      setCamOn((v) => !v);
    } catch { /* ignore */ }
  };

  return (
    <div
      className="absolute bottom-24 right-3 z-30 flex flex-col gap-2"
      data-testid="plyx-guest-controls"
    >
      <button
        type="button"
        onClick={toggleMic}
        data-testid="plyx-guest-toggle-mic"
        className={
          "p-3 rounded-full shadow-lg backdrop-blur transition "
          + (micOn ? "bg-white/15 text-white" : "bg-red-600 text-white")
        }
        aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
      >
        {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
      </button>
      <button
        type="button"
        onClick={toggleCam}
        data-testid="plyx-guest-toggle-cam"
        className={
          "p-3 rounded-full shadow-lg backdrop-blur transition "
          + (camOn ? "bg-white/15 text-white" : "bg-red-600 text-white")
        }
        aria-label={camOn ? "Turn camera off" : "Turn camera on"}
      >
        {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
      </button>
      {onLeave && (
        <button
          type="button"
          onClick={onLeave}
          data-testid="plyx-guest-leave-stage"
          className="p-3 rounded-full bg-black/70 text-amber-300 border border-amber-300/40 shadow-lg backdrop-blur"
          aria-label="Leave stage"
          title="Leave stage"
        >
          <LogOut className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

/**
 * LiveAudioGate — detects whether the browser blocked LiveKit's
 * <audio> playback (Android Chrome + iOS Safari autoplay policy) and
 * surfaces a centred "🔊 Tap to hear" overlay so the viewer can
 * unblock with a single tap. Without this the viewer sees the host's
 * video but hears nothing — Tobechukwu's exact symptom on Android.
 *
 * Strategy:
 *   • Poll every `<audio>` element LiveKit's RoomAudioRenderer creates.
 *   • If any is paused / has a media error / has `muted=true`, treat
 *     audio as gated and show the overlay.
 *   • On tap, call .play() on each (we're inside a user gesture), and
 *     ensure none are muted. Hide the overlay on success.
 */
const LiveAudioGate = () => {
  const [gated, setGated] = useState(false);

  useEffect(() => {
    const checkGate = () => {
      const audios = Array.from(document.querySelectorAll("audio"));
      if (audios.length === 0) return; // RoomAudioRenderer hasn't mounted yet
      const blocked = audios.some((a) => a.paused || a.muted);
      setGated(blocked);
    };
    checkGate();
    const interval = setInterval(checkGate, 800);
    // Also listen for play/pause events on any future audio element.
    const obs = new MutationObserver(checkGate);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); obs.disconnect(); };
  }, []);

  const unblock = useCallback(async () => {
    const audios = Array.from(document.querySelectorAll("audio"));
    let ok = false;
    for (const a of audios) {
      try {
        a.muted = false;
        await a.play();
        ok = true;
      } catch { /* ignore individual failures */ }
    }
    if (ok) setGated(false);
  }, []);

  if (!gated) return null;
  return (
    <button
      type="button"
      onClick={unblock}
      data-testid="plyx-live-audio-gate"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] px-6 py-3 rounded-full bg-black/80 backdrop-blur border border-white/25 text-white text-sm font-bold inline-flex items-center gap-2 shadow-xl animate-pulse"
    >
      🔊 Tap to hear audio
    </button>
  );
};

/**
 * EngagementLayer — inside LiveKitRoom. Owns the data channel + heart
 * animations + gift drawer trigger.
 */
const EngagementLayer = ({ roomName, creatorId, onAcceptInvite, onLeaveStage }) => {
  const { user } = usePlyxAuth();
  const [hearts, setHearts] = useState([]); // [{id, x}]
  const [giftBursts, setGiftBursts] = useState([]); // [{id, emoji}]
  const [drawerOpen, setDrawerOpen] = useState(false);
  // iter-119o: When the viewer taps a participant in the Bumper
  // standings tile, we pre-target the gift drawer so their gifts land
  // on the chosen fighter (not the host). Reset after every send.
  const [bumperTargetId, setBumperTargetId] = useState(null);
  const [bumperTargetLabel, setBumperTargetLabel] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [tipTrain, setTipTrain] = useState(null); // {tipper_count, expires_at}
  const [goalEcho, setGoalEcho] = useState(null); // eager goal update from tip response
  const heartIdRef = useRef(0);
  const isBroadcaster = !!user && creatorId && user.plyx_user_id === creatorId;

  const spawnHeart = useCallback(() => {
    heartIdRef.current += 1;
    const id = heartIdRef.current;
    const x = (Math.random() - 0.5) * 60; // px offset
    setHearts((prev) => [...prev, { id, x }].slice(-30));
    setTimeout(() => {
      setHearts((prev) => prev.filter((h) => h.id !== id));
    }, 3000);
  }, []);

  const spawnGift = useCallback((emoji) => {
    heartIdRef.current += 1;
    const id = heartIdRef.current;
    setGiftBursts((prev) => [...prev, { id, emoji }].slice(-20));
    setTimeout(() => {
      setGiftBursts((prev) => prev.filter((g) => g.id !== id));
    }, 3500);
  }, []);

  const { send: sendData, message: dataMessage } = useDataChannel("plyx-engagement");

  // Receive: every viewer + the broadcaster see the same heart/gift visuals.
  useEffect(() => {
    if (!dataMessage) return;
    try {
      const txt = new TextDecoder().decode(dataMessage.payload);
      const msg = JSON.parse(txt);
      if (msg.type === "heart") {
        spawnHeart();
      } else if (msg.type === "gift" && msg.emoji) {
        spawnGift(msg.emoji);
      } else if (msg.type === "tip_train" && msg.expires_at) {
        setTipTrain({
          tipper_count: msg.tipper_count || 3,
          expires_at: msg.expires_at,
        });
      } else if (msg.type === "goal_update" && msg.goal) {
        setGoalEcho(msg.goal);
      }
    } catch {/* ignore */}
  }, [dataMessage, spawnHeart, spawnGift]);

  // Auto-clear tip-train banner when the window expires.
  useEffect(() => {
    if (!tipTrain?.expires_at) return;
    const ms = new Date(tipTrain.expires_at).getTime() - Date.now();
    if (ms <= 0) { setTipTrain(null); return; }
    const t = setTimeout(() => setTipTrain(null), ms);
    return () => clearTimeout(t);
  }, [tipTrain]);

  const sendHeart = () => {
    spawnHeart(); // optimistic local
    try {
      const payload = new TextEncoder().encode(JSON.stringify({ type: "heart" }));
      sendData?.(payload, { topic: "plyx-engagement", reliable: false });
    } catch {/* ignore */}
  };

  const handleGiftSent = ({ emoji, tip_train, goal }) => {
    if (emoji) {
      spawnGift(emoji); // optimistic local
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ type: "gift", emoji }));
        sendData?.(payload, { topic: "plyx-engagement", reliable: true });
      } catch {/* ignore */}
    }
    // Tip-train: backend already detected the trigger. Show it locally
    // and broadcast so every other client lights up at the same instant.
    if (tip_train?.active && tip_train?.expires_at) {
      setTipTrain({
        tipper_count: tip_train.tipper_count || 3,
        expires_at: tip_train.expires_at,
      });
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: "tip_train",
          tipper_count: tip_train.tipper_count,
          expires_at: tip_train.expires_at,
        }));
        sendData?.(payload, { topic: "plyx-engagement", reliable: true });
      } catch {/* ignore */}
    }
    // Stream Goal eager update from tip response.
    if (goal) {
      setGoalEcho(goal);
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: "goal_update",
          goal,
        }));
        sendData?.(payload, { topic: "plyx-engagement", reliable: true });
      } catch {/* ignore */}
    }
  };

  return (
    <>
      {/* Floating hearts */}
      <div className="pointer-events-none absolute bottom-24 right-6">
        {hearts.map((h) => (
          <Heart
            key={h.id}
            className="absolute right-0 bottom-0 w-7 h-7 text-pink-500 fill-pink-500"
            style={{
              animation: "plyxHeartFly 3s ease-out forwards",
              left: `${h.x}px`,
            }}
          />
        ))}
      </div>

      {/* Floating gift emojis */}
      <div className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2">
        {giftBursts.map((g, i) => (
          <span
            key={g.id}
            className="absolute text-5xl"
            style={{
              animation: "plyxGiftRise 3.5s ease-out forwards",
              left: `${(i % 5) * 28 - 60}px`,
              bottom: 0,
            }}
          >
            {g.emoji}
          </span>
        ))}
      </div>

      {/* Stream Goal progress bar (top centre, below the LIVE pill) */}
      <StreamGoalWidget roomName={roomName} isBroadcaster={isBroadcaster} liveProgress={goalEcho} />

      {/* Plyx Live Hour multiplier chip — only rendered during the window */}
      <div className="absolute top-4 right-4 z-30 pointer-events-none">
        <LiveHourChip roomName={roomName} />
      </div>

      {/* Tip-train banner */}
      {tipTrain && (
        <div
          data-testid="plyx-live-tip-train-banner"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
        >
          <div className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-black text-sm shadow-[0_0_40px_rgba(255,179,45,0.6)] tracking-wide flex items-center gap-2">
            <span className="text-lg leading-none animate-bounce">🔥</span>
            <span>TIP TRAIN ROLLING</span>
            <span className="text-xs font-bold bg-black/15 px-2 py-0.5 rounded-full">
              {tipTrain.tipper_count}+ tippers · jump in!
            </span>
          </div>
        </div>
      )}

      {/* iter-119l: Centred "🔊 Tap to hear audio" gate. Renders only
          when Android Chrome / iOS Safari has blocked the LiveKit
          RoomAudioRenderer's <audio> elements from autoplaying. One
          tap unblocks every audio track. Fixes Tobechukwu's "their
          video appeared, no audio" symptom (iter-119l on Android). */}
      <LiveAudioGate />

      {/* Bottom action rail (overlay) */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/90 border-t border-white/10 p-3 flex items-center justify-between gap-2 z-[55]">
        <p className="text-white/40 text-[11px]">@{user?.handle}</p>
        <div className="flex items-center gap-2">
          <button
            data-testid="plyx-live-viewer-chat-btn"
            onClick={() => setChatOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-bold transition"
          >
            <MessageSquare className="w-4 h-4" /> Chat
          </button>
          <button
            data-testid="plyx-live-viewer-gift-btn"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black text-sm font-bold transition hover:scale-105"
          >
            <Gift className="w-4 h-4" /> Send a gift
          </button>
          <button
            data-testid="plyx-live-viewer-heart-btn"
            onClick={sendHeart}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pink-600 hover:bg-pink-500 text-white text-sm font-bold transition"
          >
            <Heart className="w-4 h-4 fill-white" /> Tap
          </button>
        </div>
      </div>

      <LiveChatPanel
        roomName={roomName}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        isMod={!!user && user.plyx_user_id === creatorId}
      />

      {/* Stage management — raise hand for viewers, full panel for
          host/moderators. Also pops the accept/decline toast on invite. */}
      <PlyxStagePanel
        roomName={roomName}
        hostId={creatorId}
        isBroadcaster={isBroadcaster}
        onAcceptInvite={onAcceptInvite}
      />

      {/* Self-controls (mic / cam / leave) — only visible to participants
          with publish permission, so they auto-show for the host and for
          any viewer that just accepted a stage invite. */}
      <GuestControls onLeave={!isBroadcaster ? onLeaveStage : undefined} />

      <GiftDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setBumperTargetId(null); setBumperTargetLabel(null); }}
        liveRoomName={roomName}
        onGiftSent={handleGiftSent}
        targetUserId={bumperTargetId}
        targetLabel={bumperTargetLabel}
      />

      {/* iter-119o: Bumper Challenge — viewer sees standings tile +
          per-fighter gift buttons when a battle is active. The tap on
          a fighter pre-targets the gift drawer so coins land on that
          fighter, not the host. Real-time updates flow via the same
          plyx_hub WS channel that LiveChatPanel already listens on. */}
      <BumperBattlePanel
        roomName={roomName}
        isHost={isBroadcaster}
        hostId={creatorId}
        onGiftParticipant={(uid) => {
          // The panel only renders fighter rows when the battle is
          // active and the uid is a declared participant, so we can
          // trust the value here. Backend re-validates anyway.
          setBumperTargetId(uid);
          setBumperTargetLabel(`Fighter @${(uid || "").slice(0, 10)}`);
          setDrawerOpen(true);
        }}
      />
    </>
  );
};

const PlyxLiveViewer = () => {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = usePlyxAuth();
  const [stream, setStream] = useState(null);
  const [token, setToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When the local viewer accepts an "on stage" invite we receive a new
  // publisher token. We flip `onStage` so the LiveKitRoom remounts
  // (key={token}) with `video={true}` / `audio={true}` and the guest's
  // camera + mic are published automatically.
  const [onStage, setOnStage] = useState(false);

  const isBroadcasterPrimary = !!user && stream && user.plyx_user_id === stream.creator_id;

  const handleAcceptInvite = useCallback((payload) => {
    if (!payload?.token) return;
    setToken(payload.token);
    if (payload.livekit_url) setLivekitUrl(payload.livekit_url);
    setOnStage(true);
    toast.success("You're on stage — your camera and mic are live");
  }, []);

  const handleLeaveStage = useCallback(async () => {
    try {
      // Best-effort: tell the backend to drop us from the guests list so
      // the host's panel updates. We re-mint a viewer token and flip
      // back to subscriber mode.
      await api.post(`/plyx/live/${roomName}/leave-stage`).catch(() => {});
      const tokenRes = await api.post(`/plyx/live/${roomName}/viewer-token`);
      setToken(tokenRes.data.token);
      setLivekitUrl(tokenRes.data.livekit_url);
      setOnStage(false);
      toast.message("You left the stage");
    } catch {
      toast.error("Couldn't leave the stage — try again");
    }
  }, [roomName, user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/plyx/login", { replace: true, state: { from: `/plyx/live/${roomName}` } });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const detail = await api.get(`/plyx/live/${roomName}`);
        if (!mounted) return;
        if (detail.data.status !== "live") {
          setError("This stream has ended.");
          setLoading(false);
          return;
        }
        setStream(detail.data);
        const tokenRes = await api.post(`/plyx/live/${roomName}/viewer-token`);
        if (!mounted) return;
        setToken(tokenRes.data.token);
        setLivekitUrl(tokenRes.data.livekit_url);
      } catch (e) {
        const status = e.response?.status;
        if (status === 410) setError("This stream has ended.");
        else if (status === 404) setError("Stream not found.");
        else setError(e.response?.data?.detail || "Could not load this stream.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [roomName, user, authLoading, navigate]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0f] text-white px-6 text-center">
        <Radio className="w-10 h-10 text-white/30 mb-4" />
        <p data-testid="plyx-live-viewer-error" className="font-semibold mb-1">{error}</p>
        <Link to="/plyx/live" className="text-[#FFCC2E] text-sm font-bold mt-4 hover:underline">
          ← Browse other live streams
        </Link>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-lk-theme="default">
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center justify-between bg-black/70 backdrop-blur border-b border-white/10">
        <button
          data-testid="plyx-live-viewer-back"
          onClick={() => navigate("/plyx/live")}
          className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm font-bold"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          {stream?.is_finals && (
            <span data-testid="plyx-live-finals-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFB42D] text-black text-[10px] font-black tracking-wider">
              <Trophy className="w-3 h-3" /> FINALS
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[11px]">
            <Users className="w-3 h-3" /> {stream?.viewer_count || 0}
          </span>
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 min-h-0 relative">
        {token && livekitUrl && (
          <LiveKitRoom
            key={token /* force reconnect when promoted to publisher */}
            token={token}
            serverUrl={livekitUrl}
            connect={true}
            video={onStage}
            audio={onStage}
            // ── Viewer quality (adaptive) ─────────────────────────────
            // Lets LiveKit pick the best simulcast layer for the viewer's
            // bandwidth — 720p on wifi, 540p on patchy mobile, 360p on
            // weak connections. Without this, viewers always pull the
            // top layer and tank on flaky networks.
            options={{
              adaptiveStream: true,
              dynacast: true,
            }}
            data-lk-theme="default"
            style={{ height: "100%" }}
            onDisconnected={() => {
              toast.message("Disconnected — the stream may have ended");
            }}
          >
            {/* Play every subscribed audio track on the viewer's
                speakers. Without this the viewer sees the video but
                hears no audio from the host or any on-stage guests. */}
            <RoomAudioRenderer />
            <ViewerStage />
            <EngagementLayer
              roomName={roomName}
              creatorId={stream?.creator_id}
              onAcceptInvite={handleAcceptInvite}
              onLeaveStage={handleLeaveStage}
            />
          </LiveKitRoom>
        )}

        {/* Title overlay */}
        <div className="absolute bottom-20 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
          <p className="text-white font-black text-lg line-clamp-2">{stream?.title}</p>
          <Link
            to={`/plyx/u/${stream?.creator?.handle}`}
            className="inline-flex items-center gap-2 mt-2 text-white/90 hover:text-[#FFCC2E] pointer-events-auto"
          >
            <span className="text-sm">@{stream?.creator?.handle}</span>
            {stream?.creator?.verified && <span className="text-white/85 text-xs">✓</span>}
            {stream?.creator?.live_streak_count > 0 && (
              <StreakFire streak={stream.creator.live_streak_count} best={stream.creator.live_streak_best} />
            )}
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes plyxHeartFly {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          15% { transform: translateY(-20px) scale(1); opacity: 1; }
          100% { transform: translateY(-360px) scale(1.4) rotate(20deg); opacity: 0; }
        }
        @keyframes plyxGiftRise {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          15% { transform: translateY(-30px) scale(1.2); opacity: 1; }
          70% { transform: translateY(-260px) scale(1.5); opacity: 1; }
          100% { transform: translateY(-380px) scale(1.7) rotate(-15deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default PlyxLiveViewer;
